// @flow
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import * as queryString from 'query-string';
import transformIncludes from '@justpark/helpers/src/transformIncludes/transformIncludes';
import snakeToCamel from '@justpark/helpers/src/snakeToCamel/snakeToCamel';
import * as querystring from 'query-string';
import { showSnackbar } from './snackbar';
import type { BookingsTransformer } from '../types';
import config from '../../config/config';
import { CREATE_BOOKING_SUCCESS } from './forms/checkout';

const { stringify } = querystring;

const include = [
  'barrier_tokens',
  'listing.address',
  'listing.photos',
  'vehicle',
  'listing',
  'review',
  'driver',
  'driver_price',
  'awin'
];

const transformedInclude = [...include].map(item => snakeToCamel(item));

export const getBooking = createAsyncThunk(
  'booking/getBooking',
  async (id, thunkApi) => {
    try {
      const bareResponse = await thunkApi.extra.bookings.getBooking(
        id,
        include
      );
      const response = transformIncludes(bareResponse, transformedInclude);

      return response.data;
    } catch (e) {
      return thunkApi.rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

export const changeBookingVehicle = createAsyncThunk(
  'booking/changeBookingVehicle',
  async (vehicleId, thunkApi) => {
    try {
      const {
        booking: {
          booking: { id: bookingId }
        }
      } = thunkApi.getState();

      const bareResponse = await thunkApi.extra.bookings.changeVehicle(
        bookingId,
        vehicleId,
        include
      );
      const response = transformIncludes(bareResponse, transformedInclude);

      thunkApi.dispatch(showSnackbar('Vehicle changed'));

      return response.data;
    } catch (e) {
      return thunkApi.rejectWithValue(e?.message || '');
    }
  }
);

export const openBarrier = createAsyncThunk(
  'booking/openBarrier',
  async (id, thunkApi) => {
    try {
      await thunkApi.extra.bookings.openBarrier(id);
      return thunkApi.dispatch(showSnackbar('Barrier open'));
    } catch (e) {
      return thunkApi.rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

export const submitRating = createAsyncThunk(
  'booking/submitRating',
  async (payload, { dispatch, rejectWithValue, getState, extra }) => {
    try {
      const {
        booking: {
          rating,
          ratingComment,
          booking: { id }
        }
      } = getState();
      const {
        data: {
          listing: {
            feedback: { latestReview }
          }
        }
      } = await extra.bookings.addReview(id, rating, ratingComment);

      dispatch(showSnackbar('Thank you for your feedback'));
      const {
        id: ratingId,
        comment,
        rating: submittedRating,
        createdAt
      } = latestReview;

      return {
        id: ratingId,
        comment,
        rating: submittedRating,
        date: createdAt
      };
    } catch (e) {
      return rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

export type State = {
  isLoading: boolean,
  getBookingError: string,
  getBookingErrorCode: number,
  postPurchase: boolean,
  booking: BookingsTransformer,
  openBarrierPending: boolean,
  openBarrierError: string,
  showRatingModal: boolean,
  submitRatingPending: boolean,
  rating: ?number,
  ratingComment: string,
  submitReviewError: string,
  changeVehicleError: string,
  changeVehiclePending: boolean,
  changeVehicleModalOpen: boolean
};

export const initialState: State = {
  isLoading: false,
  getBookingError: '',
  getBookingErrorCode: 0,
  postPurchase: false,
  booking: null,
  openBarrierError: '',
  openBarrierPending: false,
  showRatingModal: false,
  submitRatingPending: false,
  rating: null,
  ratingComment: '',
  submitReviewError: '',
  changeVehicleError: '',
  changeVehiclePending: false,
  changeVehicleModalOpen: false
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    resetState: (): State => ({
      ...initialState
    }),
    closeRatingModal: (state): State => ({
      ...state,
      showRatingModal: false,
      rating: null
    }),
    openRatingModal: (state, action: PayloadAction): State => ({
      ...state,
      showRatingModal: true,
      rating: action.payload
    }),
    setRating: (state, action: PayloadAction): State => ({
      ...state,
      rating: action.payload
    }),
    setRatingComment: (state, action: PayloadAction): State => ({
      ...state,
      ratingComment: action.payload
    }),
    openChangeVehicleModal: (state: State): State => ({
      ...state,
      changeVehicleModalOpen: true,
      changeVehicleError: ''
    }),
    closeChangeVehicleModal: (state: State): State => ({
      ...state,
      changeVehicleModalOpen: false
    })
  },
  extraReducers: {
    [getBooking.pending]: (state: State): State => ({
      ...state,
      isLoading: true,
      getBookingError: '',
      getBookingErrorCode: 0,
      booking: null
    }),
    [getBooking.fulfilled]: (state: State, action: PayloadAction): State => {
      return {
        ...state,
        isLoading: false,
        booking: action.payload
      };
    },
    [CREATE_BOOKING_SUCCESS]: (state: State, action: PayloadAction): State => {
      const { statusLabel: status, ...rest } = action.payload.data;

      const updatedResponse = {
        data: {
          ...rest,
          status
        }
      };

      return {
        ...state,
        booking: transformIncludes(updatedResponse, transformedInclude).data,
        postPurchase: true
      };
    },
    [getBooking.rejected]: (state: State, action: PayloadAction): State => {
      const { message, code } = action.payload;

      return {
        ...state,
        isLoading: false,
        getBookingError: message,
        getBookingErrorCode: code
      };
    },
    [openBarrier.pending]: (state: State): State => ({
      ...state,
      openBarrierPending: true
    }),
    [openBarrier.fulfilled]: (state: State): State => ({
      ...state,
      openBarrierPending: false
    }),
    [openBarrier.rejected]: (state: State, action: PayloadAction): State => ({
      ...state,
      openBarrierPending: false,
      openBarrierError: action.payload.message
    }),
    [submitRating.pending]: (state: State): State => ({
      ...state,
      submitRatingPending: true
    }),
    [submitRating.fulfilled]: (state: State, action: PayloadAction): State => {
      return {
        ...state,
        submitRatingPending: false,
        showRatingModal: false,
        rating: null,
        ratingComment: '',
        booking: {
          ...state.booking,
          review: action.payload
        }
      };
    },
    [submitRating.rejected]: (state: State, action: PayloadAction): State => ({
      ...state,
      submitRatingPending: false,
      submitReviewError: action.payload.message
    }),
    [changeBookingVehicle.pending]: (state: State): State => ({
      ...state,
      changeVehiclePending: true
    }),
    [changeBookingVehicle.fulfilled]: (
      state: State,
      action: PayloadAction
    ): State => ({
      ...state,
      changeVehiclePending: false,
      changeVehicleError: '',
      booking: action.payload,
      changeVehicleModalOpen: false
    }),
    [changeBookingVehicle.rejected]: (
      state: State,
      action: PayloadAction
    ): State => ({
      ...state,
      changeVehiclePending: false,
      changeVehicleError: action.payload
    })
  }
});

export const {
  resetState: resetBookingState,
  openRatingModal,
  closeRatingModal,
  setRating,
  setRatingComment,
  closeChangeVehicleModal,
  openChangeVehicleModal
} = bookingSlice.actions;

export const selectBookingGetBookingError = state =>
  state[bookingSlice.name].getBookingError;
export const selectBookingGetBookingErrorCode = state =>
  state[bookingSlice.name].getBookingErrorCode;
export const selectBookingIsLoading = state =>
  state[bookingSlice.name].isLoading;
export const selectBookingId = state => state[bookingSlice.name].booking?.id;
export const selectBookingAddressFormatted = state =>
  state[bookingSlice.name].booking?.listing.address.formatted;
export const selectBookingHasBeenRated = state =>
  state[bookingSlice.name].booking.review?.rating !== undefined;
export const selectBookingRating = state =>
  state[bookingSlice.name].booking?.review?.rating || null;
export const selectBookingIsPostPurchase = state =>
  state[bookingSlice.name].postPurchase;
export const selectBookingIsPrebook = state =>
  state[bookingSlice.name].booking?.type === 'PREBOOK';
export const selectBookingIsComplete = state =>
  state[bookingSlice.name].booking?.status === 'past';
export const selectBookingShowMentionMe = state => {
  const isPrebook = selectBookingIsPrebook(state);

  if (!isPrebook) {
    return false;
  }

  const isPostPurchase = selectBookingIsPostPurchase(state);
  const rating = selectBookingRating(state);

  return isPostPurchase || rating > 3;
};
export const selectBookingShowInsuranceModal = state => {
  const isPrebook = selectBookingIsPrebook(state);
  const isPostPurchase = selectBookingIsPostPurchase(state);

  return isPrebook && isPostPurchase;
};
export const selectBookingMentionMeUrl = state => {
  const {
    id: bookingId,
    driver: { firstName, lastName, email },
    driverPrice: { value, currency }
  } = state[bookingSlice.name].booking;

  const { postPurchase } = state[bookingSlice.name];

  const mentionMeUrl = `https://tag${
    process.env.RELEASE_STAGE === 'production' ? '' : '-demo'
  }.mention-me.com/api/v2/referreroffer/mm210f5a98`;

  const queryParms = {
    segment: 'driver',
    surname: lastName,
    firstname: firstName,
    email,
    order_number: bookingId,
    order_total: value,
    order_currency: currency,
    situation: postPurchase ? 'postpurchase' : 'bookingdetails'
  };

  return `${mentionMeUrl}?${stringify(queryParms)}`;
};
export const selectBookingListingTitle = state =>
  state[bookingSlice.name].booking?.listing.title;
export const selectBookingListingId = state =>
  state[bookingSlice.name].booking?.listing.id;
export const selectIsMonthly = state =>
  state[bookingSlice.name].booking?.bookingType === 'MONTHLY';
export const selectBookingStartDate = state =>
  state[bookingSlice.name].booking?.startDate;
export const selectBookingEndDate = state =>
  state[bookingSlice.name].booking?.endDate;
export const selectBookingVehicle = state =>
  state[bookingSlice.name].booking?.vehicle;
export const selectBookingStatus = state =>
  state[bookingSlice.name].booking?.status;
export const selectBookingType = state =>
  state[bookingSlice.name].booking?.type;
export const selectBookingIsP2P = state =>
  state[bookingSlice.name].booking?.listing.isP2P;
export const selectIsDriveup = state =>
  selectBookingType(state) === 'PAY_ON_ARRIVAL';
export const selectBookingInfinite = state =>
  selectBookingType(state) === 'INFINITE';
export const selectBookingIsUpcoming = state =>
  selectBookingStatus(state) === 'upcoming';
export const selectBookingIsActive = state =>
  selectBookingStatus(state) === 'active';
export const selectBookingIsPast = state =>
  selectBookingStatus(state) === 'past';
export const selectBookingIsUpcomingOrActive = state =>
  selectBookingIsActive(state) || selectBookingIsUpcoming(state);
export const selectIsBookingCancelled = state =>
  selectBookingStatus(state) === 'cancelled';
export const selectIsStartStop = state =>
  selectBookingType(state) === 'START_STOP';
export const selectIsPreBook = state =>
  selectBookingType(state) === 'PREBOOK' || selectBookingInfinite(state);
export const selectIsDriveUp = state =>
  selectBookingType(state) === 'PAY_ON_ARRIVAL';
export const selectBookingIsOngoing = state =>
  (selectIsStartStop(state) && !selectBookingEndDate(state)) ||
  selectBookingInfinite(state);
export const selectBookingIsVehicleEditable = state => {
  if (!selectBookingIsUpcomingOrActive(state)) {
    return false;
  }
  const { startDate, timezone } = state[bookingSlice.name].booking;
  const timeZone = timezone || 'Europe/London';
  const now = moment.tz(timeZone);

  const timeDiff = moment.tz(startDate, timeZone).diff(now, 'minutes');

  const preBook = selectIsPreBook(state);
  const isDriveup = selectIsDriveUp(state);
  const isStartStop = selectIsStartStop(state);

  const recentDriveupOrStartStop =
    (isDriveup || isStartStop) && Math.abs(timeDiff) < 5;

  return recentDriveupOrStartStop || preBook;
};

export const selectIsPreBookActiveOrUpcoming = state =>
  selectIsPreBook(state) && selectBookingIsUpcomingOrActive(state);

export const selectBookingIsExtendable = state =>
  selectIsPreBookActiveOrUpcoming(state);

export const selectBookingAccessInstructions = state => {
  const accessInfo = state[bookingSlice.name].booking?.accessInstructions || '';

  return accessInfo.length > 0
    ? accessInfo
    : 'No additional access information available. Simply navigate to the space and park';
};
export const selectBookingListingPhotos = state =>
  state[bookingSlice.name].booking?.listing.photos;
export const selectBookingListingHasPhotos = state =>
  state[bookingSlice.name].booking?.listing?.photos?.length > 0;
export const selectListingStreetViewData = state =>
  state[bookingSlice.name].booking?.listing.streetviewData;
export const selectBookingSecurityCode = state => {
  const code = state[bookingSlice.name].booking?.securityCode;

  // Handle legacy codes in teh DB that have been set to 0
  return code === '0' ? undefined : code;
};
export const selectBookingListingBarrierPin = state =>
  state[bookingSlice.name].booking?.barrierTokens?.[0]?.pin;
export const selectBookingListingHasFob = state =>
  state[bookingSlice.name].booking?.listing.fobEntry;
export const selectBookingListingHasPermit = state =>
  state[bookingSlice.name].booking?.listing.permitEntry;
export const selectBookingIsEvCharging = state =>
  state[bookingSlice.name].booking?.evCharging;
export const selectBookingBarcode = state =>
  state[bookingSlice.name].booking?.barcode;
export const selectShouldBookAgain = state =>
  selectIsPreBook(state) && !selectBookingIsUpcomingOrActive(state);
export const selectBookingAwinImageUrl = state => {
  const bookingId = state[bookingSlice.name].booking?.id;
  const { amount, customerType, voucher, channel, currency } = state[
    bookingSlice.name
  ].booking.awin;

  const qs = {
    tt: 'ns',
    tv: 2,
    merchant: config.awinAdvertiserId,
    amount,
    ref: bookingId,
    parts: `${customerType}:${amount}`,
    vc: voucher,
    ch: channel,
    testmode: config.isProduction ? 0 : 1,
    cr: currency
  };

  return `https://www.awin1.com/sread.img?${queryString.stringify(qs)}`;
};
export const selectBookingAwinConversionData = state => {
  const bookingId = state[bookingSlice.name].booking?.id;
  const { amount, customerType, voucher, channel, currency } = state[
    bookingSlice.name
  ].booking?.awin;

  return {
    amount,
    currency,
    channel,
    orderRef: bookingId,
    parts: `${customerType}:${amount}`,
    test: config.isProduction ? 0 : 1,
    voucher
  };
};
export const selectIsOpenBarrierPending = state =>
  state[bookingSlice.name].openBarrierPending;

export const selectShowOpenBarrier = state => {
  const { startDate, timezone, endDate } = state[bookingSlice.name].booking;
  const timeZone = timezone || 'Europe/London';
  const now = moment.tz(timeZone);

  const shouldOpenBarrier =
    (selectBookingIsUpcomingOrActive(state) ||
      selectBookingIsComplete(state)) &&
    selectBookingListingBarrierPin(state) !== undefined;
  const endDateMoment = moment.tz(endDate, timeZone);
  const startDateMoment = moment.tz(startDate, timeZone);
  return (
    shouldOpenBarrier &&
    now.isBetween(
      startDateMoment.subtract(15, 'minutes'),
      endDateMoment.add(15, 'minutes'),
      'minutes'
    )
  );
};
export const selectBookingShowRatingModal = state =>
  state[bookingSlice.name].showRatingModal;
export const selectBookingAddRating = state => state[bookingSlice.name].rating;
export const selectBookingAddRatingComment = state =>
  state[bookingSlice.name].ratingComment;
export const selectBookingSubmitRatingPending = state =>
  state[bookingSlice.name].submitRatingPending;
export const selectBookingShouldShowPreBookConfirmation = state => {
  const isPrebook = selectBookingIsPrebook(state);
  const isPostPurchase = selectBookingIsPostPurchase(state);

  return isPrebook && isPostPurchase;
};
export const selectBookingChangeVehicleError = state =>
  state[bookingSlice.name].changeVehicleError;
export const selectBookingChangeVehiclePending = state =>
  state[bookingSlice.name].changeVehiclePending;
export const selectBookingChangeVehicleModalOpen = state =>
  state[bookingSlice.name].changeVehicleModalOpen;

export default bookingSlice;
